"use client";

import { getSearchParam } from "@/lib/utils/hooks";
import { Anchor, Box, Button, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import Link from "next/link";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { handleResponseError } from "../../../../lib/strapi/strapiApi";
import { setNotification } from "../../../components/notifications";
import FieldLabel from "../../profile/components/inputs/FieldLabel";
import FieldTextValue from "../../profile/components/inputs/FieldTextValue";
import { emailRequirements } from "../../register/types";
import { useAuthStyles } from "../components/AuthContentWrapper";

export default function ForgotPasswordLayout() {
  const { classes: authClasses } = useAuthStyles();
  const userEmail = getSearchParam("userEmail");
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  // **************** Functions
  const submitHandler = async (values: any) => {
    try {
      setLoading(true);
      if (!executeRecaptcha) {
        throw new Error(
          "Our system was not able to verify your human status. Please check you are connected to the internet. If so, try again now!"
        );
      }
      const captchaToken = await executeRecaptcha("ForgotPasswordSubmit");
      const res = await fetch("/api/strapi/forgotPassword", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: values.email,
          captchaToken,
        }),
      });
      const response = await res.json();
      if (!res.ok) {
        throw new Error(handleResponseError(response));
      }
      setCompleted(true);
    } catch (err: any) {
      setNotification("Password", false, err.message, "error");
    }
    return setLoading(false);
  };

  const form = useForm({
    initialValues: {
      email: userEmail ?? "",
    },
    validate: (values) => {
      return {
        email: emailRequirements.test(values.email)
          ? null
          : "Invalid email format",
      };
    },
  });

  return (
    <>
      {completed ? (
        <Box className={authClasses.mainContent}>
          <Text ta="center">
            {`If you are registered with us under the email ${form.values.email}, an email has been sent to reset your password`}
          </Text>
          <Button
            title="Back to Login"
            className={authClasses.ctaButton}
            variant="outline"
            color="darkblue"
            component={Link}
            href="/login"
          >
            Back to Login
          </Button>
        </Box>
      ) : (
        <form
          onSubmit={form.onSubmit((values) => submitHandler(form.values))}
          className={authClasses.mainContent}
        >
          <Box h={80}>
            <FieldTextValue
              label="Email Address"
              editing={true}
              formProps={form.getInputProps("email")}
            />
          </Box>
          <Box className={authClasses.ctaWrapper}>
            <Button
              name="Send"
              className={authClasses.ctaButton}
              type="submit"
              loading={loading}
              variant="outline"
              color="darkblue"
              disabled={completed}
            >
              Send Email
            </Button>
            <Anchor
              href="/login"
              component={Link}
              className={authClasses.inputLink}
              tabIndex={-1}
            >
              Back to Login
            </Anchor>
          </Box>
        </form>
      )}
    </>
  );
}
